import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ImageThumb from "@/components/ImageThumb";
import { getRecentlyViewed } from "@/lib/recentlyViewed";
import { X, Clock } from "lucide-react";
import { toast } from "sonner";

const DISMISS_KEY = "ycs_still_interested_dismissed";

// A gentle "Still interested?" nudge for the most recent item a shopper viewed but didn't buy.
export default function StillInterested() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [item, setItem] = useState(null);

  useEffect(() => {
    const ids = getRecentlyViewed();
    if (!ids.length) return;
    const id = ids[0];
    let dismissed = "";
    try { dismissed = localStorage.getItem(DISMISS_KEY) || ""; } catch {}
    if (dismissed === id) return;
    api.get("/listings/by-ids", { params: { ids: id } })
      .then((r) => { if (r.data && r.data[0]) setItem(r.data[0]); })
      .catch(() => {});
  }, []);

  if (!item) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, item.id); } catch {}
    setItem(null);
  };

  const saveForLater = async () => {
    if (!user) { nav("/auth"); return; }
    try {
      await api.post(`/likes/${item.id}/save-for-later`);
      toast.success("Saved for later — find it under Saved › Save for later");
      dismiss();
    } catch {
      toast.error("Couldn't save this item");
    }
  };

  return (
    <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-10" data-testid="still-interested">
      <div className="hairline p-4 flex items-center gap-4" style={{ borderColor: "#1E3A2B" }}>
        <Link to={`/product/${item.id}`} className="shrink-0">
          <div className="w-16 h-16 hairline overflow-hidden">
            <ImageThumb path={item.image_paths?.[0]} className="w-full h-full object-cover" />
          </div>
        </Link>
        <div className="min-w-0 flex-1">
          <div className="label-eyebrow inline-flex items-center gap-1.5" style={{ color: "#1E3A2B" }}>
            <Clock size={12} /> Still interested?
          </div>
          <Link to={`/product/${item.id}`} className="font-display text-lg truncate block mt-0.5 hover:underline">
            {item.title}
          </Link>
          <div className="text-xs text-neutral-500">${Number(item.price).toFixed(2)} · pick up where you left off</div>
        </div>
        <button onClick={saveForLater} className="btn-outline text-[0.7rem]" data-testid="still-interested-save">Save for later</button>
        <Link to={`/product/${item.id}`} className="btn-primary text-[0.7rem]" data-testid="still-interested-view">View again</Link>
        <button onClick={dismiss} className="p-2 hover:bg-black/5" title="Dismiss" data-testid="still-interested-dismiss">
          <X size={16} />
        </button>
      </div>
    </section>
  );
}
